import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { Authentication } from '@/core/store/global/app-context-types';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';

import PreviewContent from './Content/PreviewContent';
import HighlightExpandedView from './HighlightExpandedView';

export type Highlight = {
  _id: string;
  content: {
    _id: string;
    contentType: string;
    content: {
      url?: string;
      text?: string;
      metadata?: any;
    };
  };
  createdBy: string;
  userInfo: {
    Name: string;
    DisplayPic: string;
    Email: string;
    Handle: string;
  };
};

type HighlightProps = {
  highlights: Highlight[];
  showDelete?: boolean;
  onPost?: (id: string) => void;
  onDelete?: (id: string) => void;
};

const HighLights: FC<HighlightProps> = ({ highlights, onPost, showDelete, onDelete }) => {
  const [showExpanded, setShowExpanded] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [showSliders, setShowSliders] = useState<boolean>(false);
  const authentication: Authentication = useContextSelector(AppContext, authSelector);
  const ref = useRef<HTMLDivElement>(null);

  const onScroll = (toLeft: boolean) => {
    const scroll = toLeft ? -250 : 250;
    ref.current?.scrollBy({
      left: scroll,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const checkScroll = () => {
      if (ref.current) {
        setShowSliders(ref.current?.scrollWidth > ref.current?.clientWidth);
      }
    };

    checkScroll();

    window.addEventListener('resize', checkScroll);
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [highlights]);

  return (
    <>
      <div className="w-full h-auto flex relative">
        <div
          ref={ref}
          className="w-full h-auto flex gap-x-3 md:gap-x-5 overflow-x-auto overflow-y-hidden custom-scrollbar-invisible p-2"
        >
          {highlights.map((chat, index) => {
            return (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  setShowExpanded(true);
                }}
                className="flex-shrink-0 flex flex-col gap-y-1 w-[60px] md:w-[72px]"
              >
                <div className="flex-shrink-0 flex flex-col relative w-[60px] h-[60px] md:w-[72px] md:h-[72px] rounded-full shadow overflow-hidden">
                  <div className="w-full h-full flex z-20 pointer-events-none bg-slate-100 rounded-full overflow-hidden">
                    <PreviewContent content={chat.content.content} contentType={chat.content.contentType} />
                  </div>
                </div>
                <span className="w-full truncate text-sm md:text-base">{chat?.userInfo?.Handle}</span>
              </button>
            );
          })}
        </div>

        {showSliders && (
          <>
            <button
              className="absolute top-1/2 left-0 -translate-y-[100%] z-20 w-7 h-7 md:flex hidden rounded-full p-1 bg-white shadow-xl"
              onClick={() => onScroll(true)}
            >
              <ChevronLeft className="w-full h-full" />
            </button>

            <button
              className="absolute top-1/2 right-0 -translate-y-[100%] z-20 w-7 h-7 hidden md:flex rounded-full p-1 bg-white shadow-xl"
              onClick={() => onScroll(false)}
            >
              <ChevronRight className="w-full h-full" />
            </button>
          </>
        )}
      </div>

      <HighlightExpandedView
        show={showExpanded}
        setShow={setShowExpanded}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        highlights={highlights}
      />

      {showExpanded && onPost && (
        <button
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex bg-black py-2 px-6 text-white rounded-full border-white border"
          onClick={() => onPost(highlights[currentIndex].content?._id)}
        >
          Post
        </button>
      )}

      {showExpanded &&
        (showDelete || authentication?.userId == highlights[currentIndex].createdBy) &&
        onDelete && (
          <button
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex bg-black py-2 px-6 text-white rounded-full border-white border"
            onClick={() => onDelete(highlights[currentIndex]._id)}
          >
            Remove
          </button>
        )}
    </>
  );
};

export default HighLights;
